import { useQuery } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../../shared/api/client";
import type { WatchlistView } from "../../shared/types/api";

/**
 * The way into the watchlist, and how many of its items have moved.
 *
 * <p>A count of items rather than of changes. "Four" meaning four pieces of work have something
 * new on them is a number somebody can act on; the same badge meaning forty-one individual
 * events across four items is a number nobody reads twice.
 *
 * <p>Nothing here is a notification. The bell says what was sent to you; this says what has
 * happened to the work you are following, whether or not anybody thought to tell you.
 */
export function WatchlistButton() {
  const navigate = useNavigate();

  /*
   * Deliberately quiet. This button is in the topbar, so it is mounted on every screen in the
   * application, and anything it does on mount it does on every navigation. A minute of
   * staleness means moving between pages costs no request at all, and the count is still fresh
   * whenever somebody comes back to the tab.
   *
   * <p>Not thrift for its own sake: a request in flight when a navigation tears the page down is
   * how the single-flight refresh guard in shared/api/client gets bypassed, and a refresh token
   * presented twice signs the person out of everything. A badge is not worth that risk.
   */
  const watchlist = useQuery({
    queryKey: ["watchlist"],
    queryFn: () => api.get<WatchlistView>("/api/v1/watchlist"),
    staleTime: 60_000,
  });

  const unread = watchlist.data?.itemsWithUnread ?? 0;

  return (
    <button
      type="button"
      onClick={() => navigate("/watching")}
      className="relative rounded-lg p-2 text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-800"
      title="Work you are watching"
      aria-label={
        unread > 0
          ? `Watching — ${unread} item${unread === 1 ? "" : "s"} with something new`
          : "Watching"
      }
    >
      <Eye className="h-5 w-5" />
      {unread > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold text-white">
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </button>
  );
}
